import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Box, VStack, Text, useToast, Flex, Image, Skeleton, SkeletonText } from '@chakra-ui/react';
import { FiAlertTriangle, FiArrowRight, FiCalendar } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { scheduleCollabReminder, acceptCollabNotification } from 'services/flaskService';
import { collabApi } from 'services/api/collab';
import SlotCardGrid from './components/SlotCardGrid';
import LocationSearchBox from './components/LocationSearchBox';
import PartySizeSelector from './components/PartySizeSelector';
import RescheduleCollabWarningModal from './components/RescheduleCollabWarningModal';
import Within24HoursErrorModal from './components/Within24HoursErrorModal';
import ExtensionRequestModal from '../components/ExtensionRequestModal';
import WizardHeader from './components/WizardHeader';
import WizardBottomBar from './components/WizardBottomBar';
import StepTurnaround, { getTurnaroundOptions } from './components/StepTurnaround';
import StepReview from './components/StepReview';
import SingleLocationConfirm from './components/SingleLocationConfirm';
import ScheduledSuccessScreen from './components/ScheduledSuccessScreen';
import { stepTitleProps, wizardTokens } from './styles';
import {
  initializeSchedulingData,
  isSubmitDisabled,
  getLocationInfo,
  convertToLocalTime,
  getTimezone,
  formatDateForReminder,
  canRescheduleCollab,
  getRescheduleBlockedMessage,
  getRemainingReschedules,
  getVisitContentDue,
} from './utils';
import { IN_STORE_TURNAROUND_DAYS } from 'constants/turnaroundConstants';
import { INFLUENCER_RESCHEDULE_CUTOFF_HOURS, deriveTurnaroundDays, goLiveKind, goLiveWindowFor, isDeliveryOnlyUgc, resolveTurnaroundDays } from '@foodfluence/shared';
import { isWithinRescheduleCutoffOrAfter, getRescheduleCutoffBlockedMessage, isRescheduleBlocked, getRescheduleBlockedReason } from './utils/rescheduleUtils';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { isClientHandledCredit, isInAppCredit } from '../utils/collabUtils';
import { hasRecentExtensionRequest, getEffectiveEndDate, getCollabAvailableTimes } from 'utils/collabUtils';
import { getMaxPartySize } from './utils/partySizeUtils';
import { useSchedulingAvailability, isSchedulingCapacityExhausted } from 'hooks/useSchedulingAvailability';
import { scheduleSlotKeyToPickerDate, pickerDateToScheduleSlotKey } from '@foodfluence/shared';
import { CollabDataContext } from 'contexts/collabDataContext';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { InfluencerCampaignDataContext } from 'contexts/InfluencerCampaignDataContext';
import { useCampaignLocations } from 'hooks/useCampaignLocations';
import { requiresBrandReview } from 'utils/campaignUtils';
import useCrisp from 'hooks/useCrisp';
import { getInfluencerPrimaryHandle } from 'utils/influencerUtils';
import { formatFirebaseDate, toDate } from 'utils/dateUtils';
import PostByConfirm, { getGoLiveConfirmation, goLiveDateRefusalMessage, usePostByConfirm } from '../components/PostByConfirm';
import { getPostByNoSlotsMessage } from '../utils/postByNoSlots';
import { deadlineLabels } from '../utils/postByLabels';

interface SelectedLocation {
  docId: string;
  fullAddress: string;
}

interface ScheduledSummary {
  timeLabel: string;
  locationLabel: string;
  isReschedule: boolean;
  postByLabel: string;
}

// Board BookVisit header step names, by wizard screen.
const STEP_NAMES: Record<'party' | 'location' | 'time' | 'turnaround' | 'review', string> = {
  party: 'Party size',
  location: 'Location',
  time: 'Time',
  turnaround: 'Turnaround',
  review: 'Review',
};

const SLOT_DATE: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' };

const ScheduleCollabPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation | null>(null);
  const [initialDate, setInitialDate] = useState<Date | null>(null);
  const [initialTime, setInitialTime] = useState<string>('');
  const [initialPartySize, setInitialPartySize] = useState<number | null>(null);
  const [initialLocation, setInitialLocation] = useState<SelectedLocation | null>(null);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [partySize, setPartySize] = useState<number | null>(null);
  const [turnaroundDays, setTurnaroundDays] = useState<number | null>(null);
  const [initialTurnaroundDays, setInitialTurnaroundDays] = useState<number | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [scheduledSummary, setScheduledSummary] = useState<ScheduledSummary | null>(null);
  // Tracks which collab the scheduling state has been initialized for (init runs once per collab).
  const initializedCollabRef = useRef<string | null>(null);
  const [isRescheduleWarningOpen, setIsRescheduleWarningOpen] = useState(false);
  const [isWithin24HoursErrorOpen, setIsWithin24HoursErrorOpen] = useState(false);
  const [isExtensionRequestModalOpen, setIsExtensionRequestModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [frozenCollab, setFrozenCollab] = useState<any>(null);
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  const { collabId } = location.state || {};
  const campaign = useContext(InfluencerCampaignDataContext)[collabId];
  const {influencerData} = useContext(InfluencerDataContext);
  const clients = useContext(ClientDataContext);
  const client = clients[campaign?.clientID];
  const collabsData = useContext(CollabDataContext);
  const currentCollab = collabsData.find(c => c.id === collabId);

  // Use frozen collab during submission, otherwise use current collab
  const collab = frozenCollab || currentCollab;

  const { openCrisp } = useCrisp(influencerData?.email_address);

  const isPendingAcceptance = collab?.status === "pendingInfluencerApproval";
  // Parsed, not merely present: a blank/garbage stored value (older docs carry "" here) used to
  // count as "scheduled", which forced the wizard onto Review with no date to show — a blank page.
  const isDateAlreadyScheduled = !!toDate(collab?.scheduledCollabDate);
  const isClientRescheduleRequest = collab?.clientRequestedReschedule;

  // Memoize the locationIds array to prevent recreation on every render
  const locationIds = useMemo(() => {
    return campaign?.foodfluenceLocations || [];
  }, [campaign?.foodfluenceLocations]);

  // Optimize: Only fetch proposed location IDs if they exist, otherwise fetch all campaign locations
  const { locations, loading: locationsLoading } = useCampaignLocations(
    collab?.proposedSelectedLocationIds && collab.proposedSelectedLocationIds.length > 0
      ? collab.proposedSelectedLocationIds
      : locationIds
  );

  const effectiveLocationId = selectedLocation?.docId ?? collab?.selectedLocation;

  // Fetch once locations have loaded. We do NOT require the selected location's timezone to
  // be present in the local cache: the backend resolves the timezone itself (location's tz,
  // else the browser tz the client always sends). Gating on tz-in-cache silently blocked all
  // fetches — including "show more" — whenever a location without a cached tz was selected.
  const availabilityEnabled =
    !!campaign &&
    !!collab &&
    !(locationIds.length > 0 && locationsLoading);

  const {
    timeOptions,
    hasBookableSlots,
    hasMoreSlots,
    expanded,
    showMore,
    loading: availabilityLoading,
    error: availabilityError,
    usesBackendSlots,
  } = useSchedulingAvailability(
    campaign,
    collab,
    locations,
    effectiveLocationId,
    availabilityEnabled
  );

  const schedulingFullyBooked = isSchedulingCapacityExhausted(
    campaign,
    collab,
    hasBookableSlots,
    usesBackendSlots
  );

  // The window the creator must post within: the turnaround locked on the collab; before
  // acceptance the campaign's; otherwise (legacy, unlocked) the in-store default — the same
  // resolution the backend applies on reschedule.
  const maxTurnaroundDays =
    collab?.contentTurnaroundDays ??
    (isPendingAcceptance && campaign ? resolveTurnaroundDays(campaign) : IN_STORE_TURNAROUND_DAYS);

  useEffect(() => {
    if (!collab || !campaign) return;
    // Initialize ONCE per collab, as soon as collab+campaign exist. Do NOT wait on locations:
    // the party step needs no locations, so waiting let this run fire late (after the creator had
    // already picked a party size) and reset partySize to null, greying Continue. collab/campaign
    // also get fresh object refs on every Firestore onSnapshot tick — the guard blocks re-runs
    // that would otherwise wipe in-progress selections. Location preselect is handled below once
    // locations load.
    if (initializedCollabRef.current === collab.id) return;
    initializedCollabRef.current = collab.id;

    const initialData = initializeSchedulingData(collab, campaign, locations, maxTurnaroundDays);
    setSelectedDate(initialData.selectedDate);
    setSelectedTime(initialData.selectedTime);
    setSelectedLocation(initialData.selectedLocation);
    setPartySize(initialData.initialPartySize);
    setInitialDate(initialData.initialDate);
    setInitialTime(initialData.initialTime);
    setInitialPartySize(initialData.initialPartySize);
    setInitialLocation(initialData.initialLocation);
    setInitialTurnaroundDays(initialData.initialTurnaroundDays);
    setLocationSearchQuery(initialData.locationSearchQuery);

    // Reschedule (self-serve or client-requested): land on Review instead of Step 1 — the
    // creator's prior selections are already restored above, so re-walking every step adds
    // nothing. Editing a specific field from Review jumps back into the normal step flow.
    // Requires an actually-restored slot: a brand-requested reschedule clears the date, and Review
    // with nothing selected renders an empty page — those cases start at Step 1 instead.
    const hasRestoredSlot = !!initialData.selectedDate && !!initialData.selectedTime;
    if ((isDateAlreadyScheduled || isClientRescheduleRequest) && hasRestoredSlot) {
      setStepIndex(steps.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isDateAlreadyScheduled/isClientRescheduleRequest derive from collab (already a dep); steps is declared later in this component and can't be referenced here.
  }, [collab, campaign, locations]);

  // Resolve the preselected / single-location default once locations finish loading — but never
  // clobber a location the creator has already chosen.
  useEffect(() => {
    if (!collab || !campaign || locations.length === 0 || selectedLocation) return;
    const { selectedLocation: initLoc } = initializeSchedulingData(collab, campaign, locations);
    if (initLoc) setSelectedLocation(initLoc);
  }, [collab, campaign, locations, selectedLocation]);

  // Pre-select the backend's recommended slot on a first booking. Skips reschedules
  // (which keep their previous-date pre-fill) and never clobbers an existing selection.
  useEffect(() => {
    if (!usesBackendSlots || isDateAlreadyScheduled || selectedTime) return;
    for (const [dateKey, slots] of timeOptions) {
      const recommended = slots.find((s) => s.recommended);
      if (recommended) {
        setSelectedDate(scheduleSlotKeyToPickerDate(dateKey));
        setSelectedTime(recommended.time);
        break;
      }
    }
  }, [timeOptions, usesBackendSlots, isDateAlreadyScheduled, selectedTime]);

  /**
   * The brand set explicit dates and times rather than opening weekly hours, so whatever is on
   * offer is the whole of it — there is nothing else to ask for.
   *
   * Deliberately not gated on there being a single slot: the point of the message is that the times
   * are the brand's fixed choice, which holds whether they published one or several.
   *
   * Declared up here with the other hooks — the success screen returns early further down, so a
   * memo placed near its usage would be called conditionally.
   */
  const hasFixedDateAndTime = useMemo(() => {
    if (!collab || !campaign) return false;
    return getCollabAvailableTimes(collab, campaign).hasSpecificTimeIntervals;
  }, [collab, campaign]);

  // Ordered wizard screens. Party size is its own counted step; turnaround hosts the final
  // confirm and is UNCOUNTED (progress bar stays full there, matching the Figma "3 of 3").
  const hasLocationStep = !!(campaign?.foodfluenceLocations && campaign.foodfluenceLocations.length > 0);
  // Go-live: each visit's deadline is the draft due (capped before go-live) or go-live end, by kind.
  const deadlineKind = goLiveWindowFor(collab, campaign) ? goLiveKind(campaign, collab) : null;
  const dueLabels = deadlineKind ? deadlineLabels(deadlineKind) : null;
  // Go-live without drafts: one fixed posting window, so there is no "how soon will you post" step.
  const skipTurnaround = deadlineKind === 'post';
  const steps = useMemo<Array<'party' | 'location' | 'time' | 'turnaround' | 'review'>>(
    () => [
      'party' as const,
      ...(hasLocationStep ? ['location' as const] : []),
      'time' as const,
      ...(skipTurnaround ? [] : ['turnaround' as const]),
      'review' as const,
    ],
    [hasLocationStep, skipTurnaround]
  );
  // Go-live accept: the window ticked on the review page; a refusal shows the checkbox on the
  // review step with the new dates, keeping the slot the creator picked.
  const goLive = usePostByConfirm(
    isPendingAcceptance ? campaign : null,
    collab,
    collab ? getGoLiveConfirmation(collab.id, location.state) : undefined
  );
  const currentStep = steps[stepIndex];
  // Count every step (including review) so "Step X of Y" reflects the real position.
  const progressCount = steps.length;
  // A brand push (extension) moves the due date; the "post by" shown and sent must include it.
  const extensionDays = collab?.contentDueDateExtensionDays ?? 0;

  // Keep stepIndex in range if `steps` changes shape (e.g. campaign/locations load late and add a
  // location step), so the pointer can never land past the end of the list.
  useEffect(() => {
    if (stepIndex > steps.length - 1) setStepIndex(steps.length - 1);
  }, [steps.length, stepIndex]);

  // Default-select the turnaround as soon as one is missing, whatever step we're on. On a
  // reschedule, pre-fill the previously chosen turnaround (so the content-due date is preserved,
  // not reset to max); otherwise default to the max allowed (baseline full-window; picking sooner
  // is opt-in).
  //
  // Deliberately NOT gated on reaching the turnaround step. A reschedule lands straight on Review,
  // whose Confirm guard requires a non-null turnaround — so gating this left Confirm disabled even
  // after the creator changed their slot, the only escape being a detour through the turnaround
  // step. Restoring a prior value leaves it equal to the baseline, so this does not by itself mark
  // the form changed; for a collab predating chosenPostTurnaroundDays it does, which is correct —
  // persisting that field for the first time is a real change.
  useEffect(() => {
    // Wait for collab AND campaign, the same gate the init effect uses. Both come from context and
    // are undefined on a fresh load (refresh, direct URL), and this effect is write-once — whatever
    // it sets first is locked in by the guard below. Running early would read prior as undefined,
    // so a creator's stored 3-day pick would be silently rewritten to the full window on submit.
    if (!collab || !campaign) return;
    if (turnaroundDays !== null) return;
    const allowed = getTurnaroundOptions(maxTurnaroundDays);
    if (!allowed.length) return;
    // Derived from the stored visit/posting pair rather than a duplicate field — see
    // deriveTurnaroundDays. Returns null for a collab scheduled before posting dates existed,
    // or for a pair that went out of sync, and we fall back to the full window.
    const prior = deriveTurnaroundDays(
      toDate(collab.scheduledCollabDate),
      collab.scheduledPostingDate ? new Date(collab.scheduledPostingDate) : null,
      maxTurnaroundDays
    );
    setTurnaroundDays(prior && allowed.includes(prior) ? prior : Math.max(...allowed));
  }, [turnaroundDays, maxTurnaroundDays, collab, campaign]);

  const handleFinalSubmit = async () => {
    if (!collab?.id || !selectedDate || !selectedTime) return;
    if (campaign?.foodfluenceLocations?.length > 0 && !selectedLocation) return;

    setIsSubmitting(true);
    // Freeze the current collab state to prevent UI updates during submission
    setFrozenCollab(currentCollab);

    const timezone = getTimezone(selectedLocation?.docId, locations);
    // A located campaign must schedule in the VENUE's timezone. Falling back to the
    // browser zone here silently stores a wrong instant whenever the creator's device
    // zone differs from the venue's (privacy-hardened browsers even report "UTC"), so
    // fail closed instead — the availability gate normally prevents ever getting here.
    if (timezone.source === 'browser' && (campaign?.foodfluenceLocations?.length ?? 0) > 0) {
      toast({
        title: 'Unable to determine the restaurant\'s timezone',
        description: 'Please refresh and try again, or contact support if this keeps happening.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      setIsSubmitting(false);
      setFrozenCollab(null);
      return;
    }
    const { localTime, dateTime } = convertToLocalTime(selectedDate, selectedTime, timezone.value);

    try {
      if (usesBackendSlots && campaign?.id && collab?.id) {
        const validation = await collabApi.validateScheduleSlot({
          campaignId: campaign.id,
          collabId: collab.id,
          scheduledCollabDate: localTime.toISOString(),
          locationId: selectedLocation?.docId,
        });
        if (!validation.valid) {
          toast({
            title: 'Time no longer available',
            description: validation.reason || 'Please choose another time.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
          setIsSubmitting(false);
          setFrozenCollab(null);
          return;
        }
      }

      // Date write, conversation creation, and pre-visit check-in (re)scheduling all
      // happen server-side atomically — if the check-in task can't be scheduled the
      // whole call fails and surfaces here, rather than being dropped fire-and-forget.
      await collabApi.scheduleInStoreVisit({
        collabId: collab.id,
        scheduledDate: localTime.toISOString(),
        selectedLocation: selectedLocation?.docId || undefined,
        partySize,
        isAccept: isPendingAcceptance,
        timezone: timezone.value,
        turnaround: skipTurnaround ? undefined : turnaroundDays ?? undefined,
        ...(isPendingAcceptance ? { goLiveConfirmed: goLive.goLiveConfirmed } : {}),
      });

      if (isPendingAcceptance) {
        acceptCollabNotification(collab.id);
      }

      const formattedDate = formatDateForReminder(dateTime);
      const { address, locationEmails } = getLocationInfo(selectedLocation, locations);
      const isReschedule = isDateAlreadyScheduled || isClientRescheduleRequest;

      // Post deadline stays tied to the brand's max turnaround window — NOT the creator's
      // (possibly sooner) chosen turnaround, which is a brand-facing gauge only. Computed off the
      // store-local visit day at 10:00 store-tz (same basis as the slot card + the retail path).
      // A brand-approved extension replaces it when later, and go-live caps it, as on the server.
      const postDueDate = getVisitContentDue(collab, campaign, selectedDate, maxTurnaroundDays + extensionDays, timezone.value).toISOString();

      const isBrandReviewRequired = requiresBrandReview(campaign, collab);
      const campaignType = campaign?.campaignType || 'inStore';

      scheduleCollabReminder(
        collab.id,
        formattedDate,
        isReschedule,
        client.accountName,
        getInfluencerPrimaryHandle(influencerData),
        influencerData.phoneNumber,
        client.email,
        timezone.value,
        address,
        locationEmails,
        campaign.clientID,
        partySize,
        postDueDate,
        isBrandReviewRequired,
        campaignType
      ).catch(error => console.error('Error scheduling collab reminder:', error));

      // Success → show the confirmation screen (replaces the old toast + immediate redirect).
      const locationLabel = selectedLocation?.fullAddress || address || 'Your selected location';
      setScheduledSummary({
        timeLabel: `${selectedDate.toLocaleDateString('en-US', SLOT_DATE)} · ${selectedTime}`,
        locationLabel,
        isReschedule,
        postByLabel: new Date(postDueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: timezone.value }),
      });
    } catch (error) {
      console.error('Error setting collab date and time:', error);
      if (goLive.handleError(error)) return;
      // The server rejects a slot that filled up between validation and the booking write
      // (concurrent-booking race) with this exact message — surface it as unavailability,
      // not a generic failure, so the creator knows to pick another time.
      const slotTaken =
        error instanceof Error && error.message === 'Selected time is no longer available';
      // A visit after the go-live visit deadline (stale page): the server message names the date.
      const afterDeadline = goLiveDateRefusalMessage(error);
      toast(
        slotTaken || afterDeadline
          ? {
              title: 'Time no longer available',
              description: afterDeadline ?? 'That slot was just taken. Please choose another time.',
              status: 'error',
              duration: 4000,
              isClosable: true,
            }
          : {
              title: 'Error',
              description: 'Failed to set collab date and time. Please try again.',
              status: 'error',
              duration: 3000,
              isClosable: true,
            }
      );
    } finally {
      setIsSubmitting(false);
      setFrozenCollab(null);
    }
  };

  const handleSubmit = () => {
    if (isDateAlreadyScheduled && !isClientRescheduleRequest && isWithinRescheduleCutoffOrAfter(collab)) {
      setIsWithin24HoursErrorOpen(true);
      return;
    }
    if (isDateAlreadyScheduled && !isClientRescheduleRequest) {
      // Check if reschedule is allowed
      if (!canRescheduleCollab(collab)) {
        toast({
          title: 'Reschedule not allowed',
          description: getRescheduleBlockedMessage(collab),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      setIsRescheduleWarningOpen(true);
    } else {
      handleFinalSubmit();
    }
  };

  const handleLocationSelect = (loc: SelectedLocation) => {
    setSelectedLocation(loc);
    // Clear the search box on pick — the choice is shown via card highlight, and leaving the
    // address in the box would keep the list filtered to that one match (hiding "See more").
    setLocationSearchQuery('');
  };

  const handleLocationSearch = (query: string) => {
    setLocationSearchQuery(query);
  };

  const isDisabled = isSubmitDisabled({
    selectedDate,
    selectedTime,
    selectedLocation,
    partySize,
    initialDate,
    initialTime,
    initialLocation,
    initialPartySize,
    turnaroundDays,
    initialTurnaroundDays,
    campaign,
    locations
  });

  // For backend-slot campaigns the chosen slot must actually exist in what's served, so a
  // stale reschedule pre-fill or an empty slot list can never enable the confirm button.
  const selectedSlotAvailable = useMemo(() => {
    if (!usesBackendSlots) return true;
    if (!selectedDate || !selectedTime) return false;
    const daySlots = timeOptions.get(pickerDateToScheduleSlotKey(selectedDate));
    return !!daySlots?.some((s) => s.time === selectedTime);
  }, [usesBackendSlots, selectedDate, selectedTime, timeOptions]);

  const partySizeChosen = partySize !== null;
  const locationChosen = !hasLocationStep || !!selectedLocation;

  // A collab that already has a date (view details / reschedule) lands straight on Review, so the
  // steps "behind" Review were never walked. Back from Review therefore leaves the wizard (returning
  // to the collab page it was opened from); back from a step reached via Review's edit buttons
  // returns to Review.
  const landedOnReview = isDateAlreadyScheduled || isClientRescheduleRequest;

  const goBack = () => {
    if (landedOnReview) {
      if (currentStep === 'review') navigate(-1);
      else setStepIndex(steps.length - 1);
      return;
    }
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
    else navigate(-1);
  };

  // Hard blocks on a creator-initiated reschedule (max count / within-24h / past). The confirm
  // CTA is greyed out and the reason shown inline. Brand-requested reschedules are never blocked
  // (handled inside the helper via clientRequestedReschedule).
  const rescheduleBlocked = isRescheduleBlocked(collab);
  const rescheduleBlockedReason = getRescheduleBlockedReason(collab);

  const remainingReschedulesText =
    collab && isDateAlreadyScheduled && !isClientRescheduleRequest
      ? canRescheduleCollab(collab)
        ? (() => {
            const remaining = getRemainingReschedules(collab);
            return remaining === 1
              ? 'This is your final reschedule opportunity.'
              : `You have ${remaining} reschedule${remaining > 1 ? 's' : ''} remaining.`;
          })()
        : 'You have reached the maximum number of reschedules for this collab.'
      : null;

  // Per-step CTA gating.
  const canAdvance = (() => {
    if (currentStep === 'party') return partySizeChosen;
    if (currentStep === 'location') return locationChosen;
    // Require an actual slot pick: selectedSlotAvailable is hard-coded true for legacy campaigns,
    // so without the explicit date+time check a legacy user could advance with nothing selected.
    if (currentStep === 'time') return partySizeChosen && locationChosen && !!selectedDate && !!selectedTime && selectedSlotAvailable && !availabilityLoading;
    if (currentStep === 'turnaround') return turnaroundDays !== null && !availabilityLoading;
    // review (final confirm)
    return (skipTurnaround || turnaroundDays !== null) && !goLive.blockedReason && !isDisabled && !isSubmitting && selectedSlotAvailable && !availabilityLoading;
  })();

  const isFinalStep = currentStep === 'review';
  const ctaLabel = isFinalStep
    ? (isPendingAcceptance ? 'Accept and Schedule' : 'Confirm')
    : 'Continue';
  const onCtaClick = isFinalStep ? handleSubmit : () => setStepIndex(stepIndex + 1);

  // Success screen — full takeover, no wizard chrome.
  if (scheduledSummary) {
    return (
      <Flex direction="column" minH="100svh" bg="#C6F432">
        <Box w="100%" flex={1}>
          <ScheduledSuccessScreen
            timeLabel={scheduledSummary.timeLabel}
            locationLabel={scheduledSummary.locationLabel}
            isReschedule={scheduledSummary.isReschedule}
            postByLabel={scheduledSummary.postByLabel}
            postByTitle={dueLabels?.slot}
            brandName={client?.accountName || 'Your brand'}
            brandLogoUrl={client?.instagramProfilePicture}
            creatorAvatarUrl={influencerData?.profile_pic_url}
            partySizeLabel={partySize ? `${partySize} ${partySize === 1 ? 'person' : 'people'}` : '—'}
            onViewProgress={() => navigate(collabId ? `/collabs/collab-details/${collabId}` : '/collabs')}
            onHome={() => navigate('/')}
          />
        </Box>
      </Flex>
    );
  }

  const showFullyBookedOnTimeStep = currentStep === 'time' && schedulingFullyBooked;
  const postByNoSlotsMessage = getPostByNoSlotsMessage(collab, campaign, schedulingFullyBooked, getTimezone(selectedLocation?.docId, locations).value);
  const submissionDim = { opacity: isSubmitting ? 0.6 : 1, pointerEvents: (isSubmitting ? 'none' : 'auto') as any };

  const timezone = getTimezone(selectedLocation?.docId, locations);
  // No times at all: go-live may be why (dates all past the draft-review cutoff), not the brand.
  const noTimesMessage = timeOptions.size === 0 ? getPostByNoSlotsMessage(collab, campaign, true, timezone.value) : null;

  const brandName = client?.accountName || 'Your brand';
  const selfReschedule = isDateAlreadyScheduled && !isClientRescheduleRequest;
  const isReschedule = isDateAlreadyScheduled || !!isClientRescheduleRequest;
  const reviewMode: 'accept' | 'self' | 'brand' = isClientRescheduleRequest ? 'brand' : selfReschedule ? 'self' : 'accept';
  const slotLabel = (date: Date | null, time: string) => (date && time ? `${date.toLocaleDateString('en-US', SLOT_DATE)} · ${time}` : null);
  const wasLabel = slotLabel(initialDate, initialTime);
  const newLabel = slotLabel(selectedDate, selectedTime);
  const slotChanged = !!newLabel && newLabel !== wasLabel;
  const creditLabel = isInAppCredit(collab, campaign) && collab ? `$${Math.round(collab.credit)}` : undefined;
  const maxGuests = campaign ? getMaxPartySize(campaign) : null;
  const locationName = locations.find((l) => l.docId === selectedLocation?.docId)?.name || selectedLocation?.fullAddress;

  return (
    <Flex direction="column" minH="100svh" bg="white" fontFamily="display" color={wizardTokens.ink}>
      {/* Header is full-bleed (not capped) so it spans the whole viewport on wide screens; only the
          step content below is width-constrained for readability. */}
      <Box w="100%">
        <WizardHeader
          stepIndex={stepIndex}
          progressCount={progressCount}
          title={currentStep ? STEP_NAMES[currentStep] : undefined}
          onBack={goBack}
          onSupport={openCrisp}
        />
      </Box>

      <Box w="100%" maxW={{ base: '560px', lg: '720px' }} mx="auto" flex={1} display="flex" flexDirection="column">
        {/* Context card (board): brand, collab type, mode pill. */}
        <Flex m="14px 20px 0 20px" p="12px" borderRadius="16px" bg="#F2F2F0" align="center" gap="12px">
          <Flex flex="none" w="44px" h="44px" borderRadius="12px" bg="#E3E8EF" align="center" justify="center" overflow="hidden" fontSize="14px" fontWeight={600}>
            {client?.instagramProfilePicture ? (
              <Image src={client.instagramProfilePicture} alt="" w="100%" h="100%" objectFit="cover" />
            ) : (
              brandName.trim().slice(0, 2).toUpperCase()
            )}
          </Flex>
          <Flex flex={1} minW={0} direction="column" gap="2px">
            <Text fontSize="16px" fontWeight={600} lineHeight="normal" noOfLines={1}>{brandName}</Text>
            <Text fontSize="13px" color="#55555A" lineHeight="normal" noOfLines={1}>{campaign?.campaignType === 'inStore' ? 'In-store visit' : 'Visit'}{campaign?.name ? ` · ${campaign.name}` : ''}</Text>
          </Flex>
          <Flex as="span" flex="none" h="28px" px="10px" borderRadius="999px" bg={wizardTokens.ink} color="#C6F432" align="center" fontSize="12px" fontWeight={600}>
            {isPendingAcceptance ? 'Accepting' : 'Reschedule'}
          </Flex>
        </Flex>

        {/* Reschedule banner (board): which kind, and the was → new swap. */}
        {isReschedule && !isPendingAcceptance && (
          <VStack m="12px 20px 0 20px" spacing="10px" align="stretch">
            {reviewMode === 'brand' ? (
              <Flex p="12px 14px" borderRadius="14px" bg="#F1FADF" color="#2A2A2C" gap="10px" fontSize="14px" lineHeight="1.4">
                <Box as="span" flex="none" mt="1px" aria-hidden><FiCalendar size={20} strokeWidth={2.2} /></Box>
                <Text as="span"><strong>{brandName} asked for a new time.</strong> Pick one that works; this doesn't use your reschedule.</Text>
              </Flex>
            ) : remainingReschedulesText ? (
              <Flex p="12px 14px" borderRadius="14px" bg="#FFF1D6" color="#5C3900" gap="10px" fontSize="14px" lineHeight="1.4">
                <Box as="span" flex="none" mt="1px" aria-hidden><FiAlertTriangle size={20} strokeWidth={2.2} /></Box>
                <Text as="span">
                  <strong>{remainingReschedulesText}</strong> Changes lock {INFLUENCER_RESCHEDULE_CUTOFF_HOURS}h before your visit.
                </Text>
              </Flex>
            ) : null}
            {wasLabel && (
              <Flex align="stretch" gap="8px">
                <Flex flex={1} minW={0} p="10px 12px" borderRadius="14px" border="1px solid #E4E4E0" direction="column" gap="3px">
                  <Text fontSize="10px" fontWeight={600} letterSpacing="0.1em" textTransform="uppercase" color="#66666A">Was</Text>
                  <Text fontSize="14px" fontWeight={600} color="#55555A" textDecoration="line-through">{wasLabel}</Text>
                </Flex>
                <Flex as="span" flex="none" align="center" aria-hidden><FiArrowRight size={18} strokeWidth={2.4} /></Flex>
                <Flex
                  flex={1}
                  minW={0}
                  p="10px 12px"
                  borderRadius="14px"
                  direction="column"
                  gap="3px"
                  {...(slotChanged ? { bg: '#C6F432' } : { border: '1.5px dashed #9A9A9E' })}
                >
                  <Text fontSize="10px" fontWeight={600} letterSpacing="0.1em" textTransform="uppercase" color="#3E3E42">New</Text>
                  <Text fontSize="14px" fontWeight={600}>{slotChanged ? newLabel : 'Pick a time'}</Text>
                </Flex>
              </Flex>
            )}
          </VStack>
        )}

        <VStack spacing="20px" align="stretch" flex={1} p="24px 20px 24px 20px">
          {availabilityError && (
            <Text fontSize="14px" fontWeight={600} color="#A1260F">{availabilityError}</Text>
          )}

          {rescheduleBlockedReason && (
            <Box p="12px 14px" borderRadius="14px" bg="#FBE1DC" color="#A1260F">
              <Text fontSize="14px" fontWeight={600}>Reschedule not available</Text>
              <Text fontSize="14px" lineHeight="1.4" mt="2px">{rescheduleBlockedReason}</Text>
            </Box>
          )}

          {/* STEP: party size */}
          {currentStep === 'party' && (
            <VStack spacing="18px" align="stretch" w="100%" {...submissionDim}>
              <VStack spacing="8px" align="stretch">
                <Text {...stepTitleProps}>How many are coming?</Text>
                {maxGuests !== null && (
                  <Text m={0} fontSize="15px" lineHeight="1.4" color="#55555A">
                    {brandName} hosts up to {maxGuests} {maxGuests === 1 ? 'guest' : 'guests'}, including you.
                  </Text>
                )}
              </VStack>
              <PartySizeSelector campaign={campaign} selectedSize={partySize} onSizeSelect={setPartySize} />
            </VStack>
          )}

          {/* STEP: location */}
          {currentStep === 'location' && (
            <Box {...submissionDim} w="100%">
              {locationsLoading || !collab ? (
                <VStack align="stretch" spacing="10px" w="100%">
                  <Skeleton height="35px" width="70%" borderRadius="8px" />
                  <Skeleton height="52px" borderRadius="14px" />
                  <Skeleton height="80px" borderRadius="16px" />
                  <Skeleton height="80px" borderRadius="16px" />
                </VStack>
              ) : locations.length === 1 ? (
                // One-location collab: no picker, just confirm the venue (auto-selected in state).
                <SingleLocationConfirm name={locations[0].name || ''} fullAddress={locations[0].fullAddress} />
              ) : (
                <VStack spacing="16px" align="stretch" w="100%">
                  <VStack spacing="8px" align="stretch">
                    <Text {...stepTitleProps}>Pick a location</Text>
                    <Text m={0} fontSize="15px" lineHeight="1.4" color="#55555A">
                      Times are based on the timezone of the location you select.
                    </Text>
                  </VStack>
                  <LocationSearchBox
                    locations={locations}
                    locationSearchQuery={locationSearchQuery}
                    selectedLocationId={selectedLocation?.docId}
                    onLocationSearch={handleLocationSearch}
                    onLocationSelect={handleLocationSelect}
                    isLoading={locationsLoading}
                  />
                </VStack>
              )}
            </Box>
          )}

          {/* STEP: time */}
          {currentStep === 'time' && (
            <VStack spacing="16px" align="stretch">
              <VStack spacing="8px" align="stretch">
                <Text {...stepTitleProps}>Pick a time</Text>
                <Text m={0} fontSize="15px" lineHeight="1.4" color="#55555A">
                  {locationName ? `${locationName} · ` : ''}Times are shown in the venue's timezone{timezone.display ? ` (${timezone.display})` : ''}.
                </Text>
                {hasFixedDateAndTime && (
                  <Text m={0} fontSize="14px" lineHeight="1.4" color="#3E3E42">
                    This collaboration has a fixed date and time with no scheduling flexibility.
                  </Text>
                )}
              </VStack>

              {showFullyBookedOnTimeStep ? (
                <VStack p="28px 20px" borderRadius="24px" bg="#F2F2F0" spacing="10px" textAlign="center">
                  <Flex w="56px" h="56px" borderRadius="999px" bg={wizardTokens.ink} color="#C6F432" align="center" justify="center" aria-hidden>
                    <FiCalendar size={24} strokeWidth={2.2} />
                  </Flex>
                  {postByNoSlotsMessage ? (
                    <Text maxW="280px" fontSize="15px" fontWeight={600} lineHeight="1.4">{postByNoSlotsMessage}</Text>
                  ) : (
                    <>
                      <Text textStyle="display" fontSize="30px" lineHeight={1.12}>All available time slots are booked</Text>
                      <Text maxW="280px" fontSize="14px" lineHeight="1.4" color="#55555A">
                        Request an extension so the brand can add more availability.
                      </Text>
                    </>
                  )}
                  {hasLocationStep && locations.length > 1 && (
                    <Box
                      as="button"
                      type="button"
                      onClick={() => setStepIndex(steps.indexOf('location'))}
                      mt="4px"
                      h="48px"
                      px="18px"
                      border="1.5px solid #0E0E0E"
                      borderRadius="999px"
                      bg="#FFFFFF"
                      fontSize="15px"
                      fontWeight={600}
                    >
                      Try another location
                    </Box>
                  )}
                </VStack>
              ) : availabilityLoading && timeOptions.size === 0 ? (
                <VStack align="stretch" spacing="10px">
                  <SkeletonText noOfLines={1} skeletonHeight="4" width="140px" />
                  <Skeleton height="120px" borderRadius="16px" />
                  <Skeleton height="96px" borderRadius="16px" />
                  <Skeleton height="96px" borderRadius="16px" />
                </VStack>
              ) : timeOptions.size === 0 ? (
                // Not fully-booked, not loading, but no slots — explain why + what to do rather
                // than leaving a blank area (legacy campaigns never report "fully booked").
                <Box p="18px" borderRadius="18px" bg="#F2F2F0">
                  <Text fontSize="16px" fontWeight={600}>No times available right now</Text>
                  <Text fontSize="14px" lineHeight="1.4" color="#3E3E42" mt="4px">
                    {noTimesMessage ?? (
                      <>
                        This brand hasn't opened any visit times yet. Check back soon, or reach out to
                        support if you think this is a mistake.
                      </>
                    )}
                  </Text>
                </Box>
              ) : (
                <SlotCardGrid
                  timeOptions={timeOptions}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  onSelect={(date, time) => {
                    setSelectedDate(date);
                    setSelectedTime(time);
                  }}
                  hasMoreSlots={hasMoreSlots}
                  expanded={expanded}
                  onShowMore={showMore}
                  loading={availabilityLoading}
                  disabled={isSubmitting}
                  maxTurnaroundDays={maxTurnaroundDays}
                  extensionDays={extensionDays}
                  timezone={timezone.value}
                  timezoneLabel={timezone.display}
                  dueFor={deadlineKind ? (visitDate) => getVisitContentDue(collab, campaign, visitDate, maxTurnaroundDays + extensionDays, timezone.value) : undefined}
                  dueLabel={dueLabels?.slot}
                />
              )}
            </VStack>
          )}

          {/* STEP: turnaround (final confirm) */}
          {currentStep === 'turnaround' && (
            <Box {...submissionDim} w="100%">
              <StepTurnaround
                maxTurnaroundDays={maxTurnaroundDays}
                draftDueLabel={deadlineKind === 'draft' && selectedDate
                  ? getVisitContentDue(collab, campaign, selectedDate, maxTurnaroundDays + extensionDays, timezone.value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: timezone.value })
                  : undefined}
                selectedTurnaround={turnaroundDays}
                onSelectTurnaround={setTurnaroundDays}
                deliveryOnly={isDeliveryOnlyUgc(collab, campaign)}
                instructions={campaign?.brandManagedCollabLogistics?.customInstructions}
                onOpenInstructions={
                  campaign?.brandManagedCollabLogistics?.customInstructions
                    ? () =>
                        toast({
                          title: 'Instructions',
                          description: campaign.brandManagedCollabLogistics!.customInstructions,
                          status: 'info',
                          duration: 8000,
                          isClosable: true,
                        })
                    : undefined
                }
              />
            </Box>
          )}

          {/* STEP: review (final confirm) */}
          {currentStep === 'review' && selectedDate && (
            <Box {...submissionDim} w="100%">
              <StepReview
                title={isReschedule && !isPendingAcceptance ? 'Confirm your new time' : 'Review and book'}
                mode={isPendingAcceptance ? 'accept' : reviewMode}
                inAppCreditLabel={creditLabel}
                brandName={brandName}
                brandLogoUrl={client?.instagramProfilePicture}
                locationLabel={selectedLocation?.fullAddress || 'Your selected location'}
                timeLabel={`${selectedDate.toLocaleDateString('en-US', SLOT_DATE)} · ${selectedTime} ${timezone.display}`}
                partySizeLabel={partySize ? `${partySize} ${partySize === 1 ? 'person' : 'people'}` : '—'}
                postByLabel={getVisitContentDue(collab, campaign, selectedDate, maxTurnaroundDays + extensionDays, timezone.value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: timezone.value })}
                postByTitle={dueLabels?.slot}
                turnaroundLabel={!skipTurnaround && turnaroundDays ? `You'll ${deadlineKind === 'draft' ? 'submit your draft' : isDeliveryOnlyUgc(collab, campaign) ? 'deliver' : 'post'} ${turnaroundDays} day${turnaroundDays > 1 ? 's' : ''} after your visit.` : undefined}
                remainingReschedulesText={remainingReschedulesText}
                showCreditNote={isClientHandledCredit(collab, campaign)}
                onEditLocation={hasLocationStep ? () => setStepIndex(steps.indexOf('location')) : undefined}
                onEditTime={() => setStepIndex(steps.indexOf('time'))}
                onEditPartySize={() => setStepIndex(steps.indexOf('party'))}
                onEditTurnaround={skipTurnaround ? undefined : () => setStepIndex(steps.indexOf('turnaround'))}
              />
              {(goLive.changed || goLive.blockedReason) && (
                <Box mt="18px">
                  <PostByConfirm state={goLive} />
                </Box>
              )}
            </Box>
          )}
        </VStack>
      </Box>

      {/* Footer is full-bleed too (see header comment above) — its own maxW keeps the CTA
          button itself narrow and centered regardless of viewport width. */}
      {showFullyBookedOnTimeStep ? (
        <WizardBottomBar
          label={collab && hasRecentExtensionRequest(collab) ? 'Extension Requested' : 'Request Extension'}
          onClick={() => setIsExtensionRequestModalOpen(true)}
          isDisabled={!!collab && hasRecentExtensionRequest(collab)}
        />
      ) : (
        <WizardBottomBar
          label={ctaLabel}
          loadingText={isPendingAcceptance ? 'Accepting...' : 'Confirming...'}
          onClick={onCtaClick}
          isDisabled={rescheduleBlocked || !canAdvance}
          isLoading={isFinalStep && isSubmitting}
          secondary={
            <Text fontSize="13px" lineHeight="1.4" color="#55555A" textAlign="center" mt="8px">
              {isFinalStep
                ? 'Confirming holds this time for you.'
                : "Your spot isn't confirmed yet — your time is held only once you confirm."}
            </Text>
          }
        />
      )}

      <Within24HoursErrorModal
        isOpen={isWithin24HoursErrorOpen}
        onClose={() => setIsWithin24HoursErrorOpen(false)}
        message={getRescheduleCutoffBlockedMessage(collab)}
        visitLabel={wasLabel ?? undefined}
        visitSub={locationName}
      />

      <RescheduleCollabWarningModal
        isOpen={isRescheduleWarningOpen}
        onClose={() => setIsRescheduleWarningOpen(false)}
        onContinue={() => {
          setIsRescheduleWarningOpen(false);
          handleFinalSubmit();
        }}
        collab={collab}
      />

      {collab && client && (
        <ExtensionRequestModal
          isOpen={isExtensionRequestModalOpen}
          onClose={() => setIsExtensionRequestModalOpen(false)}
          clientName={client.accountName || 'the brand'}
          clientEmail={client.email || ''}
          clientId={client.id || ''}
          influencerHandle={getInfluencerPrimaryHandle(influencerData)}
          influencerPhone={influencerData?.phoneNumber || ''}
          collabId={collab.id}
          collabStatus={collab.status}
          schedulingFullyBooked={schedulingFullyBooked}
          postByNoSlotsMessage={postByNoSlotsMessage}
          collabEndDate={campaign ? formatFirebaseDate(getEffectiveEndDate(campaign, collab)) : ''}
          collab={collab}
        />
      )}
    </Flex>
  );
};

export default ScheduleCollabPage;
